import React, { useEffect, useState } from "react"

import { Helmet } from "react-helmet"
import { InfiniteHitsProvided } from "react-instantsearch-core"
import { connectInfiniteHits } from "react-instantsearch-dom"
import { useIntl } from "react-intl"

import FullImageRecipeCard from "../../Recipes/Cards/FullImageRecipeCard"
import { Button, Col, Row } from "~/components/ui"
import { Body } from "~/components/ui/Typography/Typography"
import useCategories from "~/hooks/useCategories"
import useIsClient from "~/hooks/useIsClient"
import { RecipeAlgoliaHit } from "~/models/Recipe"
import { ResultState } from "~/templates/Category/types"
import { getQueriedSearchURL } from "~/templates/Category/utils/searchUrlUtils"
import { getCategoryById } from "~/utils/createPages/categoriesUtils"

import css from "./ScrollInfiniteHits.module.scss"

type propType = InfiniteHitsProvided & {
  resultState: ResultState<RecipeAlgoliaHit>
  isFilterDisplayed: boolean
}

const InfiniteHits = ({
  hits,
  hasMore,
  refineNext,
  resultState,
  isFilterDisplayed,
}: propType) => {
  const intl = useIntl()
  const lang = intl.locale
  const isClient = useIsClient()

  const currentURL = isClient && location?.href
  const canonicalURL = getQueriedSearchURL(lang, intl)

  const [displayHits, setDisplayedHits] = useState<RecipeAlgoliaHit[]>(
    resultState.hits ?? []
  )

  const urlParams = isClient && new URLSearchParams(location?.search)
  const keyword = urlParams && urlParams.get("q")?.toLowerCase()

  const CATEGORIES = useCategories(intl.locale)
  const leastNumberOfRecipes = 5

  const isIndexablePage = (leastNumberOfRecipes: number): boolean => {
    // Flattened array of non-duplicate categoryIds
    const categoryIds = hits && [
      ...new Set(hits.map(hit => hit?.categories).flat(Infinity)),
    ]

    const categories = categoryIds
      ? categoryIds.map(categoryId => getCategoryById(categoryId, CATEGORIES))
      : null

    const urlMatchesCanonical =
      currentURL && decodeURI(currentURL.toLowerCase()) === canonicalURL

    const keywordMatchesCategoryTitle =
      keyword &&
      categories?.some(category => category?.title?.toLowerCase() === keyword)

    // Keyword matches a primary tag (used in at least n number of recipes)
    const keywordMatchesPrimaryTag =
      keyword &&
      hits?.filter(hit =>
        hit?.primary_tags
          ?.map((primary_tag: string) => primary_tag.toLowerCase())
          .includes(keyword)
      ).length >= leastNumberOfRecipes

    // Indexed by robots only if exact primary tag is matched & url corresponds to the canonical & keyword doesn't correspond to a category title
    return !!(
      keywordMatchesPrimaryTag &&
      urlMatchesCanonical &&
      !keywordMatchesCategoryTitle
    )
  }

  const meta = [
    {
      name: "robots",
      content: `${
        isIndexablePage(leastNumberOfRecipes) ? "index" : "noindex"
      },follow`,
    },
  ]

  useEffect(() => {
    if (hits.length > 0) {
      setDisplayedHits(hits)
    }
  }, [hits, hasMore, resultState])

  return (
    <>
      {keyword && <Helmet meta={meta} />}
      <div className={css.ScrollInfiniteHits}>
        <Row>
          {displayHits.map(hit => {
            return (
              <Col
                width={isFilterDisplayed ? [6, 4, 3] : [6, 4, 4]}
                key={hit.objectID}
              >
                <FullImageRecipeCard hit={hit} />
              </Col>
            )
          })}
        </Row>
        <div className={css.divButton}>
          {hasMore ? (
            <Button
              variant="secondary"
              color="original"
              disabled={!hasMore}
              onClick={refineNext}
              style={
                hasMore
                  ? { display: "block" }
                  : {
                      display: "none",
                    }
              }
            >
              {intl.formatMessage({
                id: "category/button:show-more-recipes",
              })}
            </Button>
          ) : (
            <Body variant="body3" semiBold>
              {intl.formatMessage({
                id: "category/text:no-recipes",
              })}
            </Body>
          )}
        </div>
      </div>
    </>
  )
}

const ScrollInfiniteHits = connectInfiniteHits<propType, any>(InfiniteHits)

export default ScrollInfiniteHits
